import { Tabs } from '@zillow/constellation-10';
import { ZillowAds } from './ZillowAds';
import { MetaAds } from './MetaAds';
import { type BoostWidgetProps, type ZillowAdsFormValues } from './BoostWidget.types';
import { BoostAdFlagType, numberUtils } from '@zg-rentals/rental-manager-env';

export const validateZillowAdsForm = (zillowAdFormValues: ZillowAdsFormValues) => {
  const errors = new Map<keyof ZillowAdsFormValues, string>();

  if (zillowAdFormValues.adFlag === BoostAdFlagType.CUSTOM) {
    if (zillowAdFormValues.customFlag.length > 15) errors.set('customFlag', 'Text too long');
    if (!zillowAdFormValues.customFlag.trim()) errors.set('customFlag', 'Please enter a custom flag');
  }

  if (!zillowAdFormValues.websiteUrl) {
    errors.set('websiteUrl', 'Website URL is required');
  }

  if (!zillowAdFormValues.displayRentStartingAt) {
    if (!zillowAdFormValues.customDetails.trim()) errors.set('customDetails', 'Please enter the custom details');
    else if (zillowAdFormValues.customDetails.length > 35) errors.set('customDetails', 'Text too long');
  }

  if (zillowAdFormValues.subheader && zillowAdFormValues.subheader.length > 35) {
    errors.set('subheader', 'Text too long');
  }

  return errors;
};

export const validateMetaAdsForm = (zillowAdFormValues: ZillowAdsFormValues) => {
  const errors = new Map<keyof ZillowAdsFormValues, string>();
  if (!zillowAdFormValues.adCopy) {
    errors.set('adCopy', 'Facebook/Instagram ad copy is required');
  } else if (zillowAdFormValues.adCopy.length > 225) {
    errors.set('adCopy', 'Text too long');
  }

  return errors;
};

const rentStartingAtRegex = /Rent [sS]tarting at \$[0-9],?[0-9]*\+?/;

export const isRentStartingAt = (text: string) => rentStartingAtRegex.test(text);

export const getDefaultRentStartingAt = (lowPrice: number) => {
  return `Rent starting at ${numberUtils.asCurrency(lowPrice).replace('.00', '')}`;
};

export const BoostWidget = ({
  zillowAdPreviewProps,
  zillowAdFormValues,
  setZillowAdFormValues,
  zillowAdFormErrors,
  setZillosAdFormErrors,
  metaAdPreviewProps,
  disabled,
  shouldValidateMetaAds,
  displayMediaMessage,
  title,
  address,
  description,
  onClickSeeBuildingMedia,
  hideAdFlagOptions,
  shouldHideAdFlagSelect,
  dirtyZillowAdsFields,
  setDirtyZillowAdsFields,
  displayFairHousingError,
  displayListingDescriptionModal = true,
}: BoostWidgetProps) => {
  const onChangeZillowAdFormValue = (key: keyof ZillowAdsFormValues, value: unknown) => {
    const newDirtyFields = new Map(dirtyZillowAdsFields);
    const newValues = { ...zillowAdFormValues };

    // @ts-expect-error - Dynamic key assignment
    newValues[key] = value;
    newDirtyFields.set(key, true);

    const errors = validateZillowAdsForm(newValues);
    let metaErrors: Map<keyof ZillowAdsFormValues, string> = new Map<keyof ZillowAdsFormValues, string>();

    if (shouldValidateMetaAds) metaErrors = validateMetaAdsForm(newValues);

    setZillosAdFormErrors(new Map([...errors, ...metaErrors]));
    setDirtyZillowAdsFields(newDirtyFields);
    setZillowAdFormValues(newValues, errors, metaErrors, newDirtyFields);
  };

  return (
    <Tabs.Root defaultSelected="zillow-tab" fluid>
      <Tabs.List aria-label="Default tabs example">
        <Tabs.Tab value="zillow-tab" css={{ padding: '400' }}>
          Zillow ad
        </Tabs.Tab>
        <Tabs.Tab value="meta-tab" css={{ padding: '400' }}>
          Meta ads
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="zillow-tab" css={{ padding: '0 !important' }}>
        <ZillowAds
          {...zillowAdPreviewProps}
          formValues={zillowAdFormValues}
          errors={zillowAdFormErrors}
          dirtyFields={dirtyZillowAdsFields}
          onChangeFormValue={onChangeZillowAdFormValue}
          disabled={disabled}
          displayMediaMessage={displayMediaMessage}
          title={title}
          onClickSeeBuildingMedia={onClickSeeBuildingMedia}
          hideAdFlagOptions={hideAdFlagOptions}
          shouldHideAdFlagSelect={shouldHideAdFlagSelect}
          displayFairHousingError={displayFairHousingError}
        />
      </Tabs.Panel>
      <Tabs.Panel value="meta-tab" css={{ padding: '0 !important' }}>
        <MetaAds
          {...metaAdPreviewProps}
          formValues={zillowAdFormValues}
          errors={zillowAdFormErrors}
          dirtyFields={dirtyZillowAdsFields}
          onChangeFormValue={onChangeZillowAdFormValue}
          disabled={disabled}
          displayMediaMessage={displayMediaMessage}
          shouldValidateMetaAds={shouldValidateMetaAds}
          title={title}
          address={address}
          description={description}
          onClickSeeBuildingMedia={onClickSeeBuildingMedia}
          displayFairHousingError={displayFairHousingError}
          displayListingDescriptionModal={displayListingDescriptionModal}
        />
      </Tabs.Panel>
    </Tabs.Root>
  );
};
